import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AtSign, Check } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

export default function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);

  // Poll unread count every 30s while logged in
  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let alive = true;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/me/notifications/count");
        if (alive) setUnread(Number(data?.unread || 0));
      } catch { /* ignore */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const loadList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/notifications", { params: { limit: 30 } });
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await loadList();
  };

  const markAllRead = async () => {
    try {
      await api.post("/me/notifications/mark_read", { all: true });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  const openMention = async (n) => {
    // Mark this one read, then jump to the title detail (opens DetailModal via tmdb_id).
    try {
      await api.post("/me/notifications/mark_read", { notification_ids: [n.notification_id] });
    } catch { /* ignore */ }
    setItems((prev) => prev.map((x) => (x.notification_id === n.notification_id ? { ...x, read: true } : x)));
    setUnread((u) => Math.max(0, u - (n.read ? 0 : 1)));
    setOpen(false);
    // Route: home with the detail modal query params
    navigate(`/?id=${n.tmdb_id}&type=${n.media_type}#cmt-${n.comment_id}`);
  };

  if (!user) return null;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={toggle}
        data-testid="notifications-bell"
        className="relative w-9 h-9 flex items-center justify-center rounded-full bg-[var(--tt-surface-2)] hover:bg-[#222] border border-[var(--tt-border)] transition-colors"
        title="Notifications"
      >
        <Bell className="w-4 h-4 text-[var(--tt-yellow)]" />
        {unread > 0 && (
          <span
            data-testid="notifications-unread-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--tt-primary)] text-white text-[10px] font-bold flex items-center justify-center border-2 border-black"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notifications-dropdown"
          className="absolute right-0 mt-2 w-[340px] max-w-[calc(100vw-24px)] bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-xl shadow-2xl overflow-hidden z-50"
        >
          <div className="px-4 py-3 border-b border-[var(--tt-border)] flex items-center justify-between">
            <div className="font-display font-bold text-sm flex items-center gap-2">
              <Bell className="w-3.5 h-3.5 text-[var(--tt-yellow)]" />
              Notifications
            </div>
            {items.some((n) => !n.read) && (
              <button
                onClick={markAllRead}
                data-testid="notifications-mark-all"
                className="text-[10px] uppercase tracking-wider font-bold text-[var(--tt-primary)] hover:text-[var(--tt-primary-strong)] flex items-center gap-1"
              >
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="px-4 py-6 text-xs text-[var(--tt-text-muted)] text-center">Loading…</div>
            ) : items.length === 0 ? (
              <div className="px-4 py-8 text-xs text-[var(--tt-text-muted)] text-center">
                No notifications yet.
                <div className="mt-1 text-[var(--tt-text-faint)]">
                  Others will tag you with <span className="text-[var(--tt-primary)] font-semibold">@{user.name || "you"}</span>.
                </div>
              </div>
            ) : (
              <ul>
                {items.map((n) => (
                  <li
                    key={n.notification_id}
                    onClick={() => openMention(n)}
                    data-testid={`notification-${n.notification_id}`}
                    className={`px-4 py-3 cursor-pointer border-b border-[var(--tt-border)] last:border-b-0 hover:bg-white/[0.04]
                      ${!n.read ? "bg-[var(--tt-primary)]/[0.05]" : ""}`}
                  >
                    <div className="flex items-start gap-2.5">
                      {n.by_user_picture ? (
                        <img src={n.by_user_picture} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--tt-primary)] to-[var(--tt-purple)] flex items-center justify-center text-[11px] font-bold shrink-0">
                          {(n.by_user_name || "?")[0].toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-xs">
                          <span className="font-semibold">{n.by_user_name || "Someone"}</span>
                          <span className="text-[var(--tt-text-muted)]"> mentioned you</span>
                          <span className="text-[var(--tt-text-faint)] font-mono-tt ml-1.5">· {timeAgo(n.created_at)}</span>
                        </div>
                        <div className="text-[12px] text-zinc-300 mt-0.5 line-clamp-2 break-words flex items-start gap-1">
                          <AtSign className="w-3 h-3 text-[var(--tt-primary)] mt-0.5 shrink-0" />
                          <span className="min-w-0">{n.text_preview}</span>
                        </div>
                      </div>
                      {!n.read && (
                        <span className="w-2 h-2 rounded-full bg-[var(--tt-primary)] mt-1 shrink-0" />
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
